import React from 'react';
import { BrandDefs, PokerMark, BlackjackMark, BridgeMark, WhereOnEarthMark } from '../components/BrandMarks';
import './Home.css';

const APPS = [
  {
    id: 'poker',
    name: ['Poker', 'Trainer'],
    tagline: 'Sharpen your odds game.',
    description:
      "Deal a random hand and score your percentage estimate against exact hypergeometric odds. Build the mental model, not just the habit.",
    modes: ['Hand Odds', 'Pot Odds', 'Preflop Ranges', 'Equity'],
    Mark: PokerMark,
    appStoreUrl: '#',
  },
  {
    id: 'blackjack',
    name: ['Blackjack', 'Trainer'],
    tagline: 'Know the right play, every hand.',
    description:
      "Drill basic strategy and card counting without the casino pressure. Every deal scores you against the mathematically correct play.",
    modes: ['Basic Strategy', 'Counting', 'Deviations'],
    Mark: BlackjackMark,
    appStoreUrl: '#',
  },
  {
    id: 'bridge',
    name: ['Bridge', 'Trainer'],
    tagline: 'Learn bridge from scratch.',
    description:
      "Never touched a bridge hand? Start at the very beginning — tricks, suits, high-card points, opening bids — and drill each idea until it sticks.",
    modes: ['Tricks', 'HCP', 'Opening Bids', 'Responses'],
    Mark: BridgeMark,
    appStoreUrl: '#',
  },
  {
    id: 'whereonearth',
    name: ['Where on Earth!'],
    tagline: 'Know where everything is.',
    description:
      "Spin a photographic 3D globe and tap where you think a place is. Find countries, capitals, famous cities and landmarks — scored on distance, with a fifteen-second clock rewarding a fast answer.",
    modes: ['Countries', 'Capitals', 'Cities', 'Landmarks'],
    Mark: WhereOnEarthMark,
    appStoreUrl: '#',
  },
];

export default function Home() {
  return (
    <>
      <BrandDefs />

      <section className="hero">
        <div className="container hero-inner">
          <div className="hero-text">
            <span className="section-label">iOS apps</span>
            <h1 className="hero-title">
              Sharpen your<br />
              <span className="hero-title-accent">instincts.</span>
            </h1>
            <p className="hero-body">
              Focused, distraction-free iOS apps. Trainers for poker, blackjack
              and bridge, and a photographic globe for finding places you've
              never been. Each app drills one thing well.
            </p>
            <div className="hero-actions">
              <a href="#apps" className="btn-primary">View our apps</a>
              <a href="/support" className="btn-secondary">Support</a>
            </div>
          </div>

          <div className="hero-visual" aria-hidden="true">
            <div className="hero-marks">
              <div className="hero-mark hero-mark--a"><PokerMark size={92} /></div>
              <div className="hero-mark hero-mark--b"><BlackjackMark size={104} /></div>
              <div className="hero-mark hero-mark--c"><BridgeMark size={104} /></div>
              <div className="hero-mark hero-mark--d"><WhereOnEarthMark size={92} /></div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="apps">
        <div className="container">
          <span className="section-label">Available on the App Store</span>
          <h2 className="section-title">Four focused apps.</h2>
          <p className="section-subtitle">
            Each app is built for iOS with no accounts, no ads, and no analytics.
            Open it and go.
          </p>

          <div className="apps-grid">
            {APPS.map(app => <AppCard key={app.id} app={app} />)}
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container about-inner">
          <div className="about-text">
            <span className="section-label">About</span>
            <h2 className="section-title">Built with purpose.</h2>
            <p className="about-body">
              The Pantry Life makes focused iOS apps for people who want to
              improve — not just play. Our apps strip away distractions and put
              the learning loop front and centre: see the situation, make your
              call, see why you were right or wrong. Repeat until it's instinct.
            </p>
            <p className="about-body">
              Every app is built native for iOS, uses no third-party analytics,
              and requires no account to use. Just open it and go.
            </p>
          </div>

          <div className="stat-grid" aria-hidden="true">
            <StatBox value="4" label="iOS apps" />
            <StatBox value="iOS 17+" label="Native platform" />
            <StatBox value="0" label="Accounts" />
            <StatBox value="∞" label="Practice sessions" />
          </div>
        </div>
      </section>
    </>
  );
}

function AppCard({ app }) {
  const { Mark } = app;
  return (
    <article className="app-card panel">
      <div className="app-card-mark">
        <Mark size={80} />
      </div>
      <div className="app-card-body">
        <h3 className="app-card-name">
          <span className="app-card-name-part">{app.name[0]}</span>
          {app.name[1] && (
            <span className="app-card-name-part app-card-name-part--dim">{app.name[1]}</span>
          )}
        </h3>
        <p className="app-card-tagline">{app.tagline}</p>
        <p className="app-card-desc">{app.description}</p>

        <div className="mode-chips">
          {app.modes.map(m => <span className="mode-chip" key={m}>{m}</span>)}
        </div>

        <div className="app-card-footer">
          <span className="app-card-platform">iOS 17+</span>
          <a
            href={app.appStoreUrl}
            className="btn-secondary"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Download ${app.name.join(' ')} on the App Store`}
          >
            <AppleIcon />
            App Store
          </a>
        </div>
      </div>
    </article>
  );
}

function StatBox({ value, label }) {
  return (
    <div className="stat-box panel">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}
